import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const CommandMenu = () => (
  <>
    <Card gap='$4'>
      <DocSection id='opening-the-command-menu' title='Opening the Command Palette'>
        <Paragraph size='$5'>
          The Command Palette is Guild's primary action center. It is the single place
          where you create Guilds, Events, and Presentations — there are no separate
          "Create" buttons elsewhere in the interface. It also serves as the entry
          point for{' '}
          <Link href='/docs/getting-started/search'>Search</Link>.
        </Paragraph>
        <XStack gap='$3' alignItems='center'>
          <Icon name='prompt' size='$5' color='$color' flexShrink={0} />
          <Paragraph size='$5'>
            Look for this icon in the top navigation bar. Click it to open the
            Command Palette. You can also open it with a keyboard shortcut for
            faster access.
          </Paragraph>
        </XStack>
        <Paragraph size='$5'>
          When the Command Palette opens, you will see a search input at the top and
          a list of quick actions below it. The menu also displays contextual
          suggestions based on the page you are currently viewing.
        </Paragraph>
        <DocScreenshot
          src='docs/getting-started/command-menu-open.png'
          alt='Command Palette open showing search input and quick actions: Create an Event, Create a Presentation, Create a Guild, with contextual search suggestions below'
          caption='The Command Palette with quick actions and contextual suggestions'
        />
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='quick-actions' title='Quick Actions'>
        <Paragraph size='$5'>
          The Command Palette presents three quick actions that let you create
          content immediately:
        </Paragraph>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar-add' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Create an Event</Paragraph> —
              Start a new Event for one of your Guilds. You will choose the host
              Guild, set the date and venue, and publish it for attendees to RSVP.
              See{' '}
              <Link href='/docs/events/creation'>Creating an Event</Link> for the
              full walkthrough.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Create a Presentation</Paragraph>{' '}
              — Add a new Presentation to share a talk, workshop, or resource with
              your community. See{' '}
              <Link href='/docs/presentations/creation'>Creating a Presentation</Link>{' '}
              for details.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Create a Guild</Paragraph> — Set
              up a new community from scratch. You will enter a name, description, and
              photos to get started. See{' '}
              <Link href='/docs/guilds/creation'>Creating a Guild</Link> for the full
              walkthrough.
            </Paragraph>
          </XStack>
        </YStack>
        <Paragraph size='$5'>
          These quick actions are the only way to create these entities on Guild.
          If you are looking for a "Create" button on a Guild page or an Event
          listing, head to the Command Palette instead.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='contextual-suggestions' title='Contextual Suggestions'>
        <Paragraph size='$5'>
          Below the quick actions, the Command Palette shows contextual suggestions
          that adapt to where you are on the platform. For example, when you are
          viewing a Guild page, you may see suggestions related to that Guild's
          Events or Members. These suggestions help you take action faster without
          leaving your current context.
        </Paragraph>
        <Paragraph size='$5'>
          You can also type into the search input to find anything on Guild. The
          search is AI-powered and understands natural language — try queries like
          "events about React" or "guilds in London". See{' '}
          <Link href='/docs/getting-started/search'>Search</Link> to learn more
          about what you can search for.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default CommandMenu
